import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import DefaultApiService from "../../../../../infrastructure/api/default";
import DefectCard from "../components/DefectCard";

const ContractorProjectDefectsTab = ({ executionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defects, setDefects] = useState([]);
  const [evidenceByDefect, setEvidenceByDefect] = useState({});
  const [resolvingId, setResolvingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await ContractorPortalApi.getDefects(executionId);
    if (!res?.isSuccess) {
      setError(res?.message || "خطا در بارگذاری ایرادات.");
      setDefects([]);
    } else {
      setDefects(res.data?.defects || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  const uploadEvidence = async (defectId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      const result = await DefaultApiService.uploadFile(
        files.map((file, index) => ({
          file,
          name: file.name || `evidence-${index + 1}`,
        })),
        "SLN"
      );
      if (result?.isSuccess === false) {
        toast.error(result?.message || "آپلود مدرک ناموفق بود.");
        return;
      }
      const rows = Array.isArray(result?.data) ? result.data : [];
      setEvidenceByDefect((prev) => ({
        ...prev,
        [defectId]: rows.map((r) => r.directory || r.Directory).filter(Boolean),
      }));
      toast.success("مدارک رفع ایراد بارگذاری شد.");
    } catch (err) {
      toast.error(err?.message || "خطا در آپلود مدرک.");
    }
  };

  const handleResolve = async (defect) => {
    if (!evidenceByDefect[defect.id]?.length) {
      toast.error("ابتدا مدرک رفع ایراد را بارگذاری کنید.");
      return;
    }
    setResolvingId(defect.id);
    const res = await ContractorPortalApi.resolveDefect(defect.id);
    setResolvingId(null);
    if (!res?.isSuccess) {
      toast.error(res?.message || "رفع ایراد ناموفق بود.");
      return;
    }
    toast.success("ایراد به‌عنوان رفع‌شده ثبت شد.");
    await load();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2} data-testid="contractor-defects-tab">
      <Alert severity="info">
        پیمانکار می‌تواند ایرادهای تخصیص‌یافته را رفع کند. تأیید/بستن ایراد فقط
        توسط سولارنت انجام می‌شود.
      </Alert>

      {!defects.length ? (
        <Alert severity="success" data-testid="defects-empty">
          ایراد بازی برای این پروژه وجود ندارد.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {defects.map((defect) => (
            <Grid item xs={12} md={6} key={defect.id}>
              <Stack spacing={1}>
                <DefectCard
                  defect={defect}
                  resolving={resolvingId === defect.id}
                  evidenceReady={Boolean(evidenceByDefect[defect.id]?.length)}
                  onResolve={handleResolve}
                />
                {defect.canResolve ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        مدرک رفع ایراد
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                        data-testid={`defect-evidence-${defect.id}`}
                      >
                        آپلود مدرک
                        <input
                          hidden
                          type="file"
                          accept="image/*,application/pdf"
                          multiple
                          onChange={(e) =>
                            uploadEvidence(defect.id, e.target.files)
                          }
                        />
                      </Button>
                      {evidenceByDefect[defect.id]?.length ? (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                          color="text.secondary"
                        >
                          {evidenceByDefect[defect.id].length} فایل بارگذاری شد
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                ) : null}
              </Stack>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
};

export default ContractorProjectDefectsTab;
